import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-modal';
// @ts-ignore
import Form from 'react-validation/build/form';
// @ts-ignore
import Button from 'react-validation/build/button';
import { validateForm } from './validateForm';
import { ErrorMessage, SuccessMessage } from '../../common';
import Notify from '../../common/notify';
import { ValidationInput } from '../../common/ValidationInput';
import { email as emailValidator, required } from '../../common/ValidationRules';
import { login } from '../../../actions/ClientData';
import { toggleModal, toggleSuccessMessage } from '../../../actions/ViewController';
import { SERVER_URL } from '../../../config';
import { modalTypes } from '../../../consts';
import history from '../../../history';
import { TRootState } from '../../../reducers';
import { getErrorMessages } from '../../../utils';


type TLogInProps = {
    onRequestClose: () => void;
}

export default function LogIn({
    onRequestClose,
}: TLogInProps) {

    const dispatch = useDispatch();
    const notificator = useRef(null);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [token, setToken] = useState('');
    const [error, setError] = useState(null);
    const [submitEnabled, setSubmitEnabled] = useState(true);
    const [mfaEnabled, setMfaEnabled] = useState(false);

    const formRef = useRef(null);
    const emailInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const errorMessageDiv = useRef(null);

    const {
        successMessage,
    } = useSelector((state: TRootState) => ({
        successMessage: state.view.success_message,
    }));

    useEffect(() => {
        if (error && errorMessageDiv) {
            errorMessageDiv.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [error]);

    useEffect(() => () => {
        dispatch(toggleSuccessMessage(null));
    }, []);

    const handleRegister = () => {
        dispatch(toggleModal('authModal', modalTypes.SIGNUP));
    };

    const handleForgotPassword = () => {
        dispatch(toggleModal('authModal', modalTypes.RESET_PASSWORD));
    };

    const handleFrontendRedirect = () => {
        const urlParams = new URLSearchParams(history.location.search);
        const redirectUrl = decodeURI(urlParams.get('redirect'));
        if (redirectUrl) {
            if (redirectUrl.includes('checkout')) {
                history.push({ pathname: redirectUrl, state: { mode: 'checkout' } });
            } else {
                history.push(redirectUrl);
            }
        } else {
            history.push('/');
        }
    };

    const handleBackendRedirect = () => {
        const urlParams = new URLSearchParams(history.location.search);
        const redirectUrl = decodeURI(urlParams.get('next'));
        if (redirectUrl) {
            window.location.href = `${SERVER_URL}${redirectUrl}`;
        } else {
            history.push('/');
        }
    };

    const handleContinue = (includeToken = true) => {
        setError(undefined);
        dispatch(toggleSuccessMessage(null));
        setSubmitEnabled(false);

        const rawData = {
            username: email,
            password,
            ...includeToken ? { token } : {},
        };
        
        dispatch(login(rawData, (response: any, err: any) => {
            if (response && !err) {
                if (response.status === 202) {
                    dispatch(toggleSuccessMessage(response.data.content.message));
                    setMfaEnabled(true);
                    setSubmitEnabled(true);
                    return;
                }

                const { search } = history.location;
                if (search.includes('redirect=')) {
                    handleFrontendRedirect();
                    return;
                }
                if (search.includes('next=')) {
                    handleBackendRedirect();
                    return;
                }

                const userType = response.content.user_type;
                if (userType === 'client' && history.location.pathname === '/job/create') {
                    return;
                }
                if (userType === 'agent' || userType === 'client') {
                    history.push('/operations/notifications');
                }
                window.location.href = `${SERVER_URL}`;
            }


            const fieldsRefs = {
                username: emailInputRef.current,
                password: passwordInputRef.current,
            };

            let errorMessage = validateForm(
                formRef.current,
                fieldsRefs,
                err,
                false,
                errorMessageDiv.current,
            );
            setSubmitEnabled(true);
            if (!errorMessage) {
                errorMessage = err.data?.meta?.detail;
                if (!errorMessage) {
                    errorMessage = getErrorMessages(err);
                }
            }
            setError(errorMessage);
        }));
    };

    const handleGoBack = () => {
        if (history.location.pathname === '/job/create') {
            dispatch(toggleModal('authModal', modalTypes.SIGNUP));
        } else {
            dispatch(toggleModal('authModal', null));
        }
    };

    return (
        <Modal
            className="login-modal auth-form"
            contentLabel="LogIn Modal"
            onRequestClose={onRequestClose}
            isOpen={true}
        >
            <Notify ref={notificator} />
            <p className="modal-title">Log In</p>
            <div className="modal-content">
                <Form
                    ref={formRef}
                    onSubmit={(e: Event)=> {
                        e.preventDefault();
                    }}
                >
                    <ValidationInput
                        type="email"
                        name="email"
                        value={email}
                        className="email input--round"
                        placeholder="Email address"
                        ref={emailInputRef}
                        containerClassName="email-wrapper input-wrapper"
                        errorClassName="input--invalid"
                        onChange={(e: any) => setEmail(e.target.value)}
                        validations={[required, emailValidator]}
                    />
                    <ValidationInput
                        type="password"
                        value={password}
                        name="password"
                        className="password input--round"
                        placeholder="Password"
                        ref={passwordInputRef}
                        containerClassName="password-wrapper input-wrapper"
                        errorClassName="input--invalid"
                        onChange={(e: any) => setPassword(e.target.value)}
                        validations={[required]}
                    />
                    {mfaEnabled && (
                        <>
                            <ValidationInput
                                type="text"
                                name="token"
                                value={token}
                                className="token input--round"
                                placeholder="Token"
                                containerClassName="token-wrapper input-wrapper"
                                errorClassName="input--invalid"
                                onChange={(e: any) => setToken(e.target.value)}
                                validations={[required]}
                            />
                            <p className="option">
                                Didn&apos;t receive a code?{' '}
                                <a
                                    className="nowrap-white-space"
                                    onClick={() => {
                                        setToken('');
                                        handleContinue(false);
                                    }}
                                >
                                    Resend
                                </a>
                            </p>
                        </>
                    )}
                    <div className="visible-xs">
                        <Button
                            className="continue"
                            onClick={handleContinue}
                        >
                            LOG IN
                        </Button>
                    </div>
                    {!mfaEnabled && (
                        <>
                            <p className="option">
                                Don&apos;t have an account?{' '}
                                <a
                                    className="nowrap-white-space"
                                    onClick={handleRegister}
                                >
                                    Register now
                                </a>
                            </p>
                            <p className="option-secondary">
                                Forgot your password?{' '}
                                <a
                                    className="nowrap-white-space"
                                    onClick={handleForgotPassword}
                                >
                                    Reset password
                                </a>
                            </p>
                        </>
                    )}
                    <div className="hidden-xs">
                        {submitEnabled ? (
                            <Button
                                className="continue btn--round-big"
                                onClick={handleContinue}
                            >
                                CONTINUE
                            </Button>
                        ) : (
                            <button
                                className="continue btn--round-big"
                                disabled={true}
                                type="button"
                            >
                                Logging in...
                            </button>
                        )}
                        <button
                            className="back"
                            onClick={handleGoBack}
                            type="button"
                        >
                            GO BACK
                        </button>
                    </div>
                </Form>
            </div>
            {error && (
                <div ref={errorMessageDiv}>
                    <ErrorMessage message={error}/>
                </div>
            )}
            {successMessage && (
                <SuccessMessage message={successMessage} />
            )}
        </Modal>
    );
}
